import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import FnrConfirmationPopover from "UI/FnrConfirmationPopover";

import API from "../../API";
import { useRecurringGifts } from "../../RecurringGiftsContext";
import { formatDateString } from "../../Shared/formatDateString";

export default function PauseButton({ setNoticeMessage }) {
  const { state, dispatch } = useRecurringGifts();
  const { ID, paymentDates, isPaused, paymentType, status } =
    state.editModal.currentGift;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [fetchStatus, setFetchStatus] = useState(null);
  const triggerRef = useRef();

  const paymentNoun = paymentType?.toLowerCase() || "payment";
  const firstUpcomingPaymentDate = paymentDates ? paymentDates[0] : "N/A";

  const handleInitialClick = () => {
    setShowConfirmation(!showConfirmation);
  };

  const handleCancelClick = () => {
    setShowConfirmation(false);
    setFetchStatus(null);
  };

  const handleConfirmClick = () => {
    setFetchStatus("loading");

    const action = isPaused ? "unpause" : "pause";

    API[action]({ ID })
      .post()
      .then((res) => {
        dispatch({
          type: "handleGetRecurringGiftsResponse",
          payload: res,
        });
        setShowConfirmation(false);
        setFetchStatus(null);
        setNoticeMessage(null);
      })
      .catch((err) => {
        setFetchStatus("error");
      });
  };

  const confirmationContent = () => {
    return (
      <ConfirmContent>
        {fetchStatus === "error" ? (
          <Error>
            Oh no! A problem occurred. Please refresh and try again, or contact
            help@flocknote.com for assistance.
          </Error>
        ) : null}
        <ConfirmLine1>
          Are you sure you want to {isPaused ? `resume` : `pause`} this{" "}
          {paymentNoun}?
        </ConfirmLine1>

        <ConfirmLine2>
          {isPaused ? (
            <>
              The next payment will be on{" "}
              <NoWrap>
                {formatDateString(firstUpcomingPaymentDate, {
                  includeYear: true,
                })}
              </NoWrap>
              .
            </>
          ) : (
            `No more ${paymentNoun}s will be made until it is unpaused.`
          )}
        </ConfirmLine2>
      </ConfirmContent>
    );
  };

  if (status === "expired") return null;

  return (
    <Wrapper>
      <FnrButton
        data-cy="pauseRecurringBtn"
        ref={triggerRef}
        onClick={handleInitialClick}
        size="small"
        fullWidth
      >
        {isPaused ? (
          <ButtonIcon fas fa-play />
        ) : (
          <ButtonIcon fas fa-pause-circle />
        )}
        <ButtonText>{isPaused ? "Resume" : "Pause"}</ButtonText>
      </FnrButton>

      <FnrConfirmationPopover
        show={showConfirmation}
        ref={triggerRef}
        onClose={() => setShowConfirmation(false)}
        message={confirmationContent()}
        button1={
          <FnrButton
            text={`Cancel`}
            onClick={handleCancelClick}
            disabled={fetchStatus === "loading"}
          />
        }
        button2={
          <FnrButton
            text={
              isPaused
                ? `Resume this ${paymentNoun}`
                : `Pause this ${paymentNoun}`
            }
            onClick={handleConfirmClick}
            color="blue"
            loading={fetchStatus === "loading"}
          />
        }
        style={{ maxWidth: 350, padding: 20 }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  flex: 1;
`;

const ConfirmContent = styled.div`
  line-height: 1.4;
  margin-bottom: 20px;
`;

const ConfirmLine1 = styled.div`
  font-weight: 600;
  font-size: 18px;
`;

const ConfirmLine2 = styled.div`
  margin-top: 10px;
  font-size: 15px;
  opacity: 0.8;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

const Error = styled.div`
  background-color: #f7f0b6;
  line-height: 1.2;
  font-size: 14px;
  color: #ad8114;
  font-weight: 400;
  padding: 10px 14px;
  border-radius: 7px;
  margin-bottom: 10px;
`;
